// Staff purchase-order detail + action surface. Hits GET /pos/:id which
// returns the PO row, enriched line items, notes, tracking notifications,
// and a vendor/order/project/client breadcrumb context.
//
// This is the first production surface where staff can *act*, not just view:
// send the PO to the supplier, edit the report-driving fields (next action,
// ship dates, follow-up), flag/clear an exception, and add notes. All wired
// to the existing /pos/:id/* endpoints. Proofs + shipments management land on
// follow-up PRs (this page surfaces their existence via the tracking count).
//
// Mutations are gated on admin / production_rep (matching the route roles);
// notes are open to any staff role. Linked from the production work-queue +
// the project POs tab as a new-tab destination.
import { SplitShipTo } from "@/components/po/SplitShipTo";
import { boxesText, placeLabel, shipDestinations } from "@/lib/ship-split";
import { joinMissing, missingPoShippingDetails } from "@merchd/shared";
import { Fragment, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  CheckCircle2,
  CircleDollarSign,
  Copy,
  Download,
  ExternalLink,
  Flag,
  ImageOff,
  Pencil,
  Send,
  Truck,
  X,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import {
  hasArtworkDetails,
  imprintSizeLabel,
  proofTypeLabel,
} from "@/lib/po-artwork";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { TABLE_HEAD, TD, TH } from "@/components/ui/RecordParts";
import { blanksArrived, poStageLabel, poStageTone } from "@/lib/production";
import { PoShippingSection } from "@/components/pos/PoShippingSection";
import { SupplierEmails } from "@/components/pos/SupplierEmails";
import { SentEmailsCard } from "@/components/send/SentEmailsCard";
import {
  SendToCustomerModal,
  type SendRecipientInput,
} from "@/components/send/SendToCustomerModal";
import { CARRIER_OPTIONS } from "@/lib/carriers";
import { useAuth } from "@/auth/AuthProvider";
import { MentionTextarea, NoteText } from "@/components/notes/MentionTextarea";
import { EditDeleteNote } from "@/components/notes/EditDeleteNote";
import { colorCountWords, poChargeName } from "@merchd/shared";

// CEO 2026-08-04 supersedes the round-59 opener-only default: the send dialog now
// pre-fills the WHOLE vendor email from GET /pos/:id/send-compose (rendered by
// the same server template the send route uses, artwork download lines included)
// and every word of it is editable. No client-side default string.

interface PoSendArgs {
  recipients?: SendRecipientInput[];
  /** The rep's FULL edited email (CEO 2026-08-04) — replaces the template. */
  bodyHtml?: string;
  copySender?: boolean;
  /** Send PO window (Stephanie 2026-10-14): CC / BCC / subject — one email. */
  cc?: Array<{ email: string; name?: string }>;
  bcc?: Array<{ email: string; name?: string }>;
  bccProduction?: boolean;
  subject?: string;
  skipZeroDecoration?: boolean;
  /** Second click on the missing-link soft gate → send anyway. */
  skipMissingLink?: boolean;
  /** Attachments taken off this email in the Send window. */
  leaveOff?: string[];
}

interface Po {
  id: string;
  poNumber: number;
  orderId: string;
  vendorId: string;
  poType: string;
  internalStage: string;
  exceptionFlag: boolean | null;
  exceptionReason: string | null;
  followUpFlag: boolean | null;
  nextActionType: string | null;
  nextActionDate: string | null;
  requiredShipDate: string | null;
  supplierShipDate: string | null;
  shippingMethod: string | null;
  subtotalCost: string;
  notes: string | null;
  internalNotes: string | null;
  shipFromVendorId: string | null;
  shippingAddressId: string | null;
  shippingAddressSnapshot: unknown;
  shippingAccountId: string | null;
  publicShareUuid: string;
}

interface PoLine {
  id: string;
  name: string;
  serviceType: string;
  quantity: number;
  unitCost: string;
  totalCost: string | null;
}

// Enriched, vendor-grade lines — the same shape /po/:uuid renders, so staff
// see exactly what the vendor sees (SKU + variants + decoration + artwork).
interface PoArtwork {
  fileId: string;
  filename: string;
  mimeType: string;
  url: string | null;
  proofRequired: boolean;
  notes: string | null;
}

interface PoDecorationCharge {
  chargeType: string;
  amount: string;
  billingModel: "fixed" | "per_piece";
  /** How many (null = default: fixed 1, per piece the line's units). */
  quantity?: number | null;
  note: string | null;
}

interface PoDecoration {
  id: string;
  location: string | null;
  imprintType: string | null;
  colorCount: number | null;
  inkColors: string[];
  artwork: PoArtwork | null;
  charges: PoDecorationCharge[];
  // Artwork Details (T16) — the structured spec the supplier decorates from.
  logoName: string | null;
  logoColor: string | null;
  proofType: string | null;
  logoWidthIn: string | null;
  logoHeightIn: string | null;
  useMaxSize: boolean;
  repeatLogo: string | null;
}

interface PoVariant {
  id: string;
  color: string | null;
  size: string | null;
  material: string | null;
  quantity: number;
  unitCost: string | null;
}

interface PoVendorLine {
  id: string;
  lineItemId: string;
  name: string;
  serviceType: string;
  quantity: number;
  unitCost: string;
  totalCost: string | null;
  sku: string | null;
  description: string | null;
  imageUrl: string | null;
  variants: PoVariant[];
  decorations: PoDecoration[];
  artwork: PoArtwork[];
}

interface PoNote {
  id: string;
  authorId: string;
  body: string;
  isPinned: boolean | null;
  createdAt: string | null;
}

interface PoContext {
  vendorName: string;
  orderNumber: number;
  projectId: string;
  projectName: string;
  clientId: string;
  clientName: string;
  inHandsDate: string | null;
  shipFromVendorName: string | null;
  pairedPoId: string | null;
  pairedPoNumber: number | null;
  /** The paired PO's stage — blanks arrived when delivered/billed/closed. */
  pairedPoStage: string | null;
}

interface VendorContact {
  id: string;
  name: string;
  primaryEmail: string | null;
  artEmail?: string | null;
  primaryPhone: string | null;
}

interface VendorResponse {
  vendor: VendorContact;
  /** The supplier's contacts — offered on the Send PO window's To/CC/BCC. */
  contacts?: Array<{ name: string; email: string | null; role: string | null }>;
}

// Per-line pricing sanity flag from the server (cost vs canonical supplier
// cost + vs sell price). Amber, never-blocking. Staff-only — never exposed on
// the public /po/:uuid vendor view.
type PoPricingWarningKind =
  | "zero_cost"
  | "cost_above_canon"
  | "cost_below_canon"
  | "cost_at_or_above_sell";

interface PoPricingWarning {
  lineItemId: string;
  name: string;
  kind: PoPricingWarningKind;
  lineCost: number;
  canonicalCost: number | null;
  canonSource: string | null;
  sellPrice: number | null;
}

interface PoDetailResponse {
  po: Po & { shippingQuote?: string | null };
  /** The staff-only "Shipping price" box is switched on (Jake's DDL has run). */
  shipping_quote_ready?: boolean;
  /** The ship-to as it should read — company + "Attn" (Stephanie 2026-10-27). */
  vendor_ship_to?: unknown;
  line_items: PoLine[];
  vendor_lines: PoVendorLine[];
  /** Split shipping: item → each address + quantities (Stephanie 2026-10-16). */
  ship_splits?: Record<string, Array<{ addressId: string; addressSnapshot: Record<string, unknown>; quantities: Array<{ color: string | null; size: string | null; qty: number }>; quantity: number }>>;
  // Product lines priced at $0 — surfaced so the page can warn BEFORE send.
  zero_cost_lines: string[];
  // Per-line pricing sanity warnings (cost vs supplier canon + vs sell).
  pricing_warnings: PoPricingWarning[];
  notes: PoNote[];
  tracking_notifications: unknown[];
  context: PoContext | null;
  // Creation-time gate overrides recorded on the `created` po_event — true
  // when the rep deliberately skipped the artwork / $0-cost 409 gate.
  created_flags?: {
    skip_artwork_check: boolean;
    skip_zero_cost_check: boolean;
  };
}

interface Shipment {
  id: string;
  destinationAddressId: string | null;
  destinationAddressSnapshot: unknown;
  carrier: string | null;
  trackingNumber: string | null;
  trackingUrl: string | null;
  status: string;
  shippedAt: string | null;
  deliveredAt: string | null;
  shippedQuantity: number | null;
  /** Boxes under this tracking number, and how many have landed (2026-10-19). */
  boxCount?: number | null;
  boxesDelivered?: number | null;
  notes: string | null;
  createdAt: string | null;
}

interface ShipmentsResponse {
  rows: Shipment[];
}

const NEXT_ACTION_OPTIONS: { value: string; label: string }[] = [
  { value: "needs_art_approval", label: "Needs art approval" },
  { value: "needs_data", label: "Needs data" },
  // commonsku's list, in its order (Stephanie 2026-11-02).
  { value: "waiting_on_client_response", label: "Waiting on client response" },
  { value: "firm_in_hands_date", label: "Firm in-hands date" },
  { value: "call_vendor", label: "Call vendor" },
  { value: "confirm_ship_date", label: "Confirm ship date" },
  { value: "track_package", label: "Track package" },
  { value: "confirm_order", label: "Confirm order" },
];

const EXCEPTION_OPTIONS: { value: string; label: string }[] = [
  { value: "oos", label: "Out of stock" },
  { value: "waiting_for_proof", label: "Waiting for proof" },
  { value: "needs_pre_payment", label: "Needs pre-payment" },
  { value: "vendor_delay", label: "Vendor delay" },
  { value: "artwork_hold", label: "Artwork hold" },
  { value: "proof_rejected", label: "Proof rejected" },
  { value: "shipping_issue", label: "Shipping issue" },
  { value: "price_dispute", label: "Price dispute" },
  { value: "other", label: "Other" },
];

function shipmentTone(
  s: string,
): "active" | "prospect" | "dormant" | "closed" | "warn" | "info" {
  switch (s) {
    case "pending":
      return "dormant";
    case "in_transit":
    case "out_for_delivery":
      return "info";
    case "delivered":
      return "active";
    case "exception":
      return "warn";
    case "returned":
      return "closed";
    default:
      return "info";
  }
}

function labelize(s: string | null): string {
  if (!s) return "—";
  return s.replace(/_/g, " ");
}

function formatMoney(s: string | null): string {
  if (s === null) return "—";
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string } | undefined;
    return body?.error ?? `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

export function PurchaseOrderDetailPage() {
  // Nested under /projects/:id, `id` is the PROJECT id and `poId` is the PO —
  // top-level /pos/:id has no poId. Resolve once to the document id so the
  // dozen `/pos/${id}` calls below never hit the wrong resource (2026-07-25).
  const { id: routeId, poId } = useParams<{ id: string; poId: string }>();
  const id = poId ?? routeId;
  // Nested (in-project, poId present) vs standalone (top-level /pos/:id via the
  // PoRedirect fallback for project-less production POs). When nested, the
  // ProjectDetailLayout chrome (breadcrumb band + left sub-nav) already carries
  // the client/project context, so rendering our own back-link + "client ·
  // project" line would be pure duplication — suppress both, and widen to
  // max-w-none to fill the layout's 1fr column. Standalone keeps them + the
  // max-w-6xl frame. Mirrors OrderDetailPage's density pass (CEO 2026-07-25;
  // ProjectDetailPage.tsx chrome).
  const nested = Boolean(poId);
  const container = "mx-auto w-full"; // full width (CEO 2026-10-14)
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManage =
    user?.roles.some((r) => r === "admin" || r === "production_rep") ?? false;

  const detailKey = ["pos", id, "detail"];
  const { data, isLoading, isError } = useQuery({
    queryKey: detailKey,
    queryFn: () => api<PoDetailResponse>(`/pos/${id}`),
    enabled: Boolean(id),
  });

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const [noteText, setNoteText] = useState("");
  const [noteMentions, setNoteMentions] = useState<string[]>([]);
  const [exMode, setExMode] = useState(false);
  const [exReason, setExReason] = useState("vendor_delay");
  const [exNote, setExNote] = useState("");
  // Send dialog (2026-07-30) — the recipient/message popup, mirroring the
  // estimate/SO send. The D2 $0-decoration soft gate keeps the dialog OPEN so
  // the rep's edits survive: `decoConfirmPending` flips the skip flag on, and a
  // second "Send PO" click sends anyway (no remount, nothing to re-type).
  const [showSendModal, setShowSendModal] = useState(false);
  // "Email to supplier" on the POs tab opens this page with ?send=1
  // (Stephanie 2026-10-15, PO idea 2): the Send window opens by itself once
  // the page knows the PO can go out. The ?send=1 is taken off the address
  // straight away so a refresh doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [sendIntent, setSendIntent] = useState(() => searchParams.get("send") === "1");
  useEffect(() => {
    if (searchParams.get("send") !== "1") return;
    const next = new URLSearchParams(searchParams);
    next.delete("send");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [decoConfirmPending, setDecoConfirmPending] = useState(false);
  // Missing-link soft gate (CEO 2026-08-04) — same shape as decoConfirmPending.
  const [missingLinkConfirmPending, setMissingLinkConfirmPending] =
    useState(false);
  const [sendModalError, setSendModalError] = useState<string | null>(null);
  // Soft-gate copy for the dialog's peach notice slot (decoConfirmPending's
  // "click Send PO anyway" prompt). Kept apart from sendModalError so a
  // non-blocking confirm never wears the rust error styling.
  const [sendModalNotice, setSendModalNotice] = useState<string | null>(null);
  // Copy-link affordance (once the PO is sent) + the share URL captured from the
  // send response so the honest "email failed" path is never a dead end.
  const [sentShareUrl, setSentShareUrl] = useState<string | null>(null);
  const [copiedAt, setCopiedAt] = useState<number | null>(null);
  const copied = copiedAt !== null && Date.now() - copiedAt < 2000;
  useEffect(() => {
    if (copiedAt === null) return;
    const t = setTimeout(() => setCopiedAt(null), 2100);
    return () => clearTimeout(t);
  }, [copiedAt]);
  // D6 — Cancel PO reason capture (the /pos/:id/cancel route requires a reason).
  const [cancelMode, setCancelMode] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [banner, setBanner] = useState<{
    kind: "ok" | "warn" | "err";
    msg: string;
  } | null>(null);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: detailKey });

  const shipmentsKey = ["pos", id, "shipments"];
  const { data: shipData } = useQuery({
    queryKey: shipmentsKey,
    queryFn: () => api<ShipmentsResponse>(`/pos/${id}/shipments`),
    enabled: Boolean(id),
  });
  // Has Other's-accounts setup run? (push-house-shipping-accounts.ts)
  const houseQ = useQuery({
    queryKey: ["shipping-accounts", "house"],
    queryFn: () =>
      api<{ ready: boolean; shipping_accounts?: Array<{ id: string; kind: "other" | "supplier" }> }>(`/shipping-accounts/house`),
    staleTime: 5 * 60 * 1000,
  });
  const houseReady = houseQ.data?.ready ?? false;
  const invalidateShipments = () =>
    void queryClient.invalidateQueries({ queryKey: shipmentsKey });

  const [showShipForm, setShowShipForm] = useState(false);
  const [shipCarrier, setShipCarrier] = useState("fedex");
  const [shipTracking, setShipTracking] = useState("");
  const [shipTrackingUrl, setShipTrackingUrl] = useState("");
  // Split shipping (Stephanie 2026-10-16): which address this box went to, how
  // many were in it, whether more are coming, and which "tracking to come"
  // placeholder is being filled in.
  const [shipDest, setShipDest] = useState("");
  const [shipQty, setShipQty] = useState("");
  const [shipMore, setShipMore] = useState(false);
  const [shipBoxes, setShipBoxes] = useState("");
  const [fillingId, setFillingId] = useState<string | null>(null);

  // Vendor contact — the email PO "Send" notifies. A null address is a
  // silent-failure trap (Send still marks the PO sent but emails no one), so
  // we surface + edit it inline here. The PATCH role gate is enforced
  // server-side (admin / production_rep); the input is shown to all staff.
  const vendorId = data?.po?.vendorId;
  const vendorKey = ["vendors", vendorId];
  const { data: vendorData } = useQuery({
    queryKey: vendorKey,
    queryFn: () => api<VendorResponse>(`/vendors/${vendorId}`),
    enabled: Boolean(vendorId),
  });
  const sendMutation = useMutation({
    mutationFn: (args: PoSendArgs) =>
      api<{
        public_url: string;
        email_sent: boolean;
        recipients?: string[];
        mode?: string;
        resent?: boolean;
        zero_cost_lines: string[];
        pricing_warnings: PoPricingWarning[];
      }>(`/pos/${id}/send`, {
        method: "POST",
        body: JSON.stringify({
          ...(args.recipients ? { recipients: args.recipients } : {}),
          ...(args.bodyHtml !== undefined
            ? { body_html: args.bodyHtml }
            : {}),
          ...(args.skipMissingLink ? { skip_missing_link_check: true } : {}),
          ...(args.copySender ? { copy_sender: true } : {}),
          ...(args.cc ? { cc: args.cc } : {}),
          ...(args.bcc ? { bcc: args.bcc } : {}),
          ...(args.bccProduction !== undefined ? { bcc_production: args.bccProduction } : {}),
          ...(args.subject ? { subject: args.subject } : {}),
          ...(args.leaveOff?.length ? { leave_off: args.leaveOff } : {}),
          ...(args.skipZeroDecoration
            ? { skip_zero_decoration_check: true }
            : {}),
        }),
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ["sent-emails"] });
      setDecoConfirmPending(false);
      setMissingLinkConfirmPending(false);
      setSendModalError(null);
      setSendModalNotice(null);
      setShowSendModal(false);
      setSentShareUrl(res.public_url ?? null);
      const zeroNote =
        res.zero_cost_lines && res.zero_cost_lines.length > 0
          ? ` Heads up: ${res.zero_cost_lines.length} product line(s) went out at $0 — ${res.zero_cost_lines.join(", ")}.`
          : "";
      // Fold non-zero-cost pricing sanity flags into the same warn banner
      // (zero_cost is already covered by zeroNote above, so exclude it here).
      const priceFlags = (res.pricing_warnings ?? []).filter(
        (w) => w.kind !== "zero_cost",
      );
      const priceNote =
        priceFlags.length > 0
          ? ` Pricing check: ${priceFlags.length} line(s) look off vs supplier cost — review before the supplier bills us.`
          : "";
      const anyWarn = zeroNote !== "" || priceNote !== "";
      // Honest link-only copy: no email is the RIGHT outcome when the rep sent
      // with no recipients (link shared, emailed nobody) — not a silent failure.
      const who =
        res.recipients && res.recipients.length > 0
          ? res.recipients.join(", ")
          : null;
      setBanner(
        !res.email_sent
          ? {
              // The dialog's canSubmit makes a zero-recipient send impossible, so
              // the only remaining !email_sent trigger is a mail-provider
              // failure. Say so honestly and point at the (now real) Copy link
              // button in the header.
              kind: "warn",
              msg: `${res.resent ? "PO not resent" : "PO marked sent"} — but the email failed to send. Use the Copy link button above to share it with the supplier manually.${zeroNote}${priceNote}`,
            }
          : anyWarn
            ? {
                kind: "warn",
                msg: `PO ${res.resent ? "resent" : "sent"} — emailed ${who ?? "the supplier"} a link.${zeroNote}${priceNote}`,
              }
            : {
                kind: "ok",
                msg: `PO ${res.resent ? "resent" : "sent"} — emailed ${who ?? "the supplier"} a link.`,
              },
      );
      invalidate();
    },
    onError: (e) => {
      // D2 — the decoration-pricing soft gate. Keep the dialog OPEN (so the
      // rep's recipients + message survive) and arm the skip flag: a second
      // "Send PO" click sends anyway. Surface the reason in the dialog.
      if (
        e instanceof ApiError &&
        e.status === 409 &&
        (e.body as { reason?: string } | null)?.reason ===
          "decoration_pricing_missing"
      ) {
        setDecoConfirmPending(true);
        // Soft gate, not a failure — route the copy through the dialog's peach
        // notice slot (errorMessage stays reserved for genuine errors).
        setSendModalError(null);
        setSendModalNotice(
          "This decorator PO is $0 — the quote has no decoration charges for this work. Click Send PO anyway to send it without pricing.",
        );
        return;
      }
      // Missing-link soft gate (409) — identical mechanics to the D2 confirm
      // above: dialog stays open, skip flag arms, copy goes to the peach slot.
      if (
        e instanceof ApiError &&
        e.status === 409 &&
        (e.body as { reason?: string } | null)?.reason ===
          "missing_document_link"
      ) {
        setMissingLinkConfirmPending(true);
        setSendModalError(null);
        setSendModalNotice(
          (e.body as { message?: string } | null)?.message ??
            "This email doesn't include the purchase order link — the supplier won't be able to open it. Send anyway?",
        );
        return;
      }
      // 400 with a plain-language `message` (e.g. the custom_message zod reason)
      // — surface it in the dialog rather than a dead-end code.
      const msg =
        e instanceof ApiError
          ? ((e.body as { message?: string } | null)?.message ?? errText(e))
          : errText(e);
      setSendModalError(msg);
      if (!showSendModal) setBanner({ kind: "err", msg });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (reason: string) =>
      api(`/pos/${id}/cancel`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      setBanner({ kind: "ok", msg: "PO cancelled." });
      setCancelMode(false);
      setCancelReason("");
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  // Remove one item from the PO — e.g. out of stock (Stephanie 2026-10-29).
  // The PO only: the quote and invoice keep it for the sales rep.
  const [removing, setRemoving] = useState<{ id: string; name: string; reason: string } | null>(null);
  const removeLineMutation = useMutation({
    mutationFn: (args: { id: string; name: string; reason: string }) =>
      api(`/pos/${id}/lines/${args.id}`, {
        method: "DELETE",
        body: JSON.stringify({ reason: args.reason }),
      }),
    onSuccess: (_res, args) => {
      setRemoving(null);
      setBanner({
        kind: "ok",
        msg: `${args.name} was removed from this PO. Resend the PO so the supplier gets the updated version. The quote and invoice still list it for the sales rep.`,
      });
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  // Manually start production on a confirmed PO (confirmed → in_production).
  // Normally driven by proof approval; this is the rep's direct advance / the
  // way to unstick an order whose production roll-up never fired.
  const patchMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/pos/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      setBanner({ kind: "ok", msg: "Saved." });
      setEditing(false);
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  const exceptionSet = useMutation({
    mutationFn: (body: { reason: string; note?: string }) =>
      api(`/pos/${id}/exception`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setBanner({ kind: "ok", msg: "Exception flagged." });
      setExMode(false);
      setExNote("");
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  const exceptionClear = useMutation({
    mutationFn: () => api(`/pos/${id}/exception`, { method: "DELETE" }),
    onSuccess: () => {
      setBanner({ kind: "ok", msg: "Exception cleared." });
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  const addNote = useMutation({
    mutationFn: (body: string) =>
      api(`/pos/${id}/notes`, {
        method: "POST",
        body: JSON.stringify({ body, mention_user_ids: noteMentions }),
      }),
    onSuccess: () => {
      setNoteText("");
      setNoteMentions([]);
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  // Record a shipment. initial_status='in_transit' is the common case — the
  // rep gets the tracking # by email after the vendor already shipped, so the
  // row goes straight to "in transit" (shipped_at stamped server-side). Ships
  // to the PO's shipping address.
  const resetShipForm = () => {
    setShowShipForm(false);
    setShipTracking("");
    setShipTrackingUrl("");
    setShipQty("");
    setShipMore(false);
    setShipBoxes("");
    setShipDest("");
    setFillingId(null);
  };
  const createShipmentMutation = useMutation({
    mutationFn: (body: {
      destination_address_id: string;
      carrier: string;
      tracking_number: string | null;
      tracking_url: string | null;
      shipped_quantity?: number;
      more_to_come?: boolean;
      box_count?: number;
    }) =>
      api(`/pos/${id}/shipments`, {
        method: "POST",
        body: JSON.stringify({ ...body, initial_status: "in_transit" }),
      }),
    onSuccess: () => {
      setBanner({ kind: "ok", msg: shipMore ? "Tracking added — the rest is marked “tracking to come”." : "Tracking added." });
      resetShipForm();
      invalidateShipments();
      invalidate(); // PO stage may advance in_production → shipped
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });
  // Fill in a "tracking to come" placeholder.
  const fillShipmentMutation = useMutation({
    mutationFn: (body: { shipmentId: string; carrier: string; tracking_number: string | null; tracking_url: string | null; shipped_quantity?: number; more_to_come?: boolean; box_count?: number }) =>
      api(`/shipments/${body.shipmentId}/tracking`, {
        method: "POST",
        body: JSON.stringify({ carrier: body.carrier, tracking_number: body.tracking_number, tracking_url: body.tracking_url, shipped_quantity: body.shipped_quantity, more_to_come: body.more_to_come, box_count: body.box_count }),
      }),
    onSuccess: () => {
      setBanner({ kind: "ok", msg: "Tracking added." });
      resetShipForm();
      invalidateShipments();
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });
  // Fix or delete a shipment's tracking (Stephanie 2026-10-30).
  const [editShip, setEditShip] = useState<{ id: string; carrier: string; tracking: string; url: string; boxes: string } | null>(null);
  const [confirmDeleteShip, setConfirmDeleteShip] = useState<string | null>(null);
  const editShipMutation = useMutation({
    mutationFn: (e: { id: string; carrier: string; tracking: string; url: string; boxes: string }) =>
      api(`/shipments/${e.id}/tracking-info`, {
        method: "PATCH",
        body: JSON.stringify({
          carrier: e.carrier || null,
          tracking_number: e.tracking.trim() || null,
          tracking_url: e.url.trim(),
          ...(Number(e.boxes) >= 1 ? { box_count: Number(e.boxes) } : {}),
        }),
      }),
    onSuccess: () => {
      setEditShip(null);
      setBanner({ kind: "ok", msg: "Tracking updated." });
      invalidateShipments();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });
  const deleteShipMutation = useMutation({
    mutationFn: (shipmentId: string) => api(`/shipments/${shipmentId}`, { method: "DELETE" }),
    onSuccess: () => {
      setConfirmDeleteShip(null);
      setBanner({ kind: "ok", msg: "Shipment deleted." });
      invalidateShipments();
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });
  const removePlaceholderMutation = useMutation({
    mutationFn: (shipmentId: string) => api(`/shipments/${shipmentId}`, { method: "DELETE" }),
    onSuccess: () => {
      setBanner({ kind: "ok", msg: "Removed — nothing more is coming on this PO." });
      invalidateShipments();
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  const shipTransitionMutation = useMutation({
    mutationFn: (body: {
      shipmentId: string;
      event: string;
      exception_note?: string;
    }) =>
      api(`/shipments/${body.shipmentId}/transition`, {
        method: "POST",
        body: JSON.stringify({
          event: body.event,
          ...(body.exception_note
            ? { exception_note: body.exception_note }
            : {}),
        }),
      }),
    onSuccess: () => {
      setBanner({ kind: "ok", msg: "Tracking updated." });
      invalidateShipments();
      invalidate(); // delivering the last box may advance the PO stage
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  // B1 manual trigger: mark all bills received on a delivered PO. Advances
  // internal_stage delivered → billed, which unblocks commission "earned".
  const billsReceivedMutation = useMutation({
    mutationFn: () =>
      api(`/pos/${id}/bills-received`, { method: "POST" }),
    onSuccess: () => {
      setBanner({
        kind: "ok",
        msg: "All bills marked received — PO moved to Billed.",
      });
      invalidate();
    },
    onError: (e) => setBanner({ kind: "err", msg: errText(e) }),
  });

  if (isLoading) {
    return (
      <div className={container}>
        <div className="space-y-3" aria-label="Loading purchase order">
          <div className="h-9 w-48 animate-pulse rounded bg-white" />
          <div className="h-24 animate-pulse rounded-md bg-white" />
        </div>
      </div>
    );
  }

  if (isError || !data?.po) {
    return (
      <div className={container}>
        <p role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-4 text-sm text-risk">
          We couldn't load this purchase order. Nothing has changed — refresh to try again.
        </p>
        <Link
          to="/production/report"
          className="mt-4 inline-flex min-h-[40px] items-center gap-1 text-sm text-ink hover:text-muted"
        >
          <ArrowLeft className="h-4 w-4" /> Production
        </Link>
      </div>
    );
  }

  const po = data.po;
  const ctx = data.context;
  const lines = data.line_items;
  const otherLines = lines.filter((l) => l.serviceType !== "product");
  const vendorLines = data.vendor_lines ?? [];
  // Where this PO's boxes can go (its ship-to, or each split address).
  const shipDests = shipDestinations({ addressId: data.po.shippingAddressId, snapshot: data.po.shippingAddressSnapshot }, data.ship_splits);
  const fillingRow = fillingId ? (shipData?.rows ?? []).find((r) => r.id === fillingId) ?? null : null;
  const zeroCostLines = data.zero_cost_lines ?? [];
  // Non-zero-cost pricing sanity flags (zero_cost has its own dedicated banner
  // below, so exclude it here to avoid double-warning the same line).
  const pricingWarnings = (data.pricing_warnings ?? []).filter(
    (w) => w.kind !== "zero_cost",
  );
  const notes = data.notes;
  const isDraft = po.internalStage === "draft";
  const canResend = !isDraft && po.internalStage !== "cancelled" && po.internalStage !== "closed";
  // Ship method + shipping account are required to send (Stephanie 2026-10-14).
  // The account is asked for once Other's own accounts exist (houseReady).
  const missingShipping = missingPoShippingDetails(po, {
    accountRequired: houseReady,
    vendorEmail: vendorData ? (vendorData.vendor.primaryEmail ?? null) : "loading",
    shipTo: po.shippingAddressSnapshot,
    // Ship-by has to be before in-hands (Stephanie 2026-10-14).
    ...(ctx ? { inHandsDate: ctx.inHandsDate } : {}),
    // On the supplier's account no ship method is needed (Stephanie 2026-10-14).
    shipsOnSupplierAccount: (houseQ.data?.shipping_accounts ?? []).some(
      (a) => a.kind === "supplier" && a.id === po.shippingAccountId,
    ),
  });
  // No supplier email on file doesn't lock Send: a one-off address typed in the
  // Send window's To line is used for this PO only and not saved (Stephanie
  // 2026-11-02). The Order / Art email boxes save one on the supplier.
  const blockSend = missingShipping.filter((m) => m !== "supplier email");
  // ?send=1 → open the Send window once vendor + accounts have loaded, only
  // when the normal Send / Resend button would work. If shipping details are
  // missing, the page's own notice says what to add.
  if (sendIntent && vendorData !== undefined && !houseQ.isLoading) {
    setSendIntent(false);
    if (canManage && (isDraft || canResend) && blockSend.length === 0) setShowSendModal(true);
  }
  const isFlagged = po.exceptionFlag === true;
  // D6 — cancel is reachable from any non-terminal stage (server: not cancelled/
  // not closed).
  const canCancel =
    po.internalStage !== "cancelled" && po.internalStage !== "closed";
  // Vendor share link — the same /po/:uuid the email links. Prefer the URL the
  // send response returned (consumes the mutation's public_url), else build it
  // from the PO's share uuid so Copy link works on reload too. Shown once the PO
  // is out of draft (a draft PO isn't publicly viewable yet).
  const vendorShareUrl =
    sentShareUrl ?? `${window.location.origin}/po/${po.publicShareUuid}`;
  const canCopyShareLink =
    po.internalStage !== "draft" && po.internalStage !== "cancelled";

  function startEdit() {
    setForm({
      next_action_type: po.nextActionType ?? "",
      next_action_date: po.nextActionDate ?? "",
      supplier_ship_date: po.supplierShipDate ?? "",
      internal_notes: po.internalNotes ?? "",
      follow_up_flag: po.followUpFlag === true,
    });
    setEditing(true);
    setBanner(null);
    // The form opens in the Production box further down — take her there
    // (Stephanie 2026-11-02: "the button disappears and thats all that happens").
    window.setTimeout(() => {
      const el = document.getElementById("po-production");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
      el?.parentElement?.querySelector<HTMLElement>("select, input, textarea")?.focus({ preventScroll: true });
    }, 50);
  }

  async function copyShareLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedAt(Date.now());
    } catch {
      setBanner({
        kind: "err",
        msg: "Could not copy — your browser blocked clipboard access.",
      });
    }
  }

  function saveEdit() {
    // Build a patch with nullable fields (empty string → null).
    const body: Record<string, unknown> = {
      next_action_type: (form.next_action_type as string) || null,
      next_action_date: (form.next_action_date as string) || null,
      supplier_ship_date: (form.supplier_ship_date as string) || null,
      internal_notes: (form.internal_notes as string) || null,
      follow_up_flag: Boolean(form.follow_up_flag),
    };
    patchMutation.mutate(body);
  }

  // Two-step (blanks + decorator) chain state — handoff W15: "a missing
  // blank-arrival confirmation must be visible". pairedPoStage comes from
  // GET /pos/:id context (services/purchase-orders-builder.ts getPoContext).
  const isDecoration = po.poType === "decoration_service";
  // Items can come off a supplier PO until it's billed (the server says why
  // not, e.g. the only item → cancel the PO instead).
  const canRemoveLines =
    !isDecoration && !["cancelled", "closed", "billed"].includes(po.internalStage ?? "");
  const pairLive = Boolean(ctx?.pairedPoId) && ctx?.pairedPoNumber != null && po.internalStage !== "cancelled";
  const blanksHere = blanksArrived(ctx?.pairedPoStage ?? null);
  const blanksPending =
    isDecoration && pairLive && !blanksHere &&
    !["shipped", "delivered", "billed", "closed", "cancelled"].includes(po.internalStage);
  // No "Type" fact in the header (Stephanie 2026-10-27: "remove type").
  const outlineBtn =
    "inline-flex h-10 items-center gap-1.5 rounded-md border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas disabled:opacity-50";
  const primaryBtn =
    "inline-flex h-10 items-center gap-1.5 rounded-md bg-ink px-3.5 text-sm font-medium text-white hover:bg-ink-700 disabled:opacity-50";
  const card = "rounded-md border border-rule bg-white";
  const cardTitle = "text-lg font-semibold tracking-[-0.4px] text-ink";

  return (
    <div className={container}>
      {/* Breadcrumb — back to the project's POs tab (or production if no ctx).
          Suppressed when nested: ProjectDetailLayout's breadcrumb band + left
          sub-nav already provide the way back, so this would duplicate it (same
          rationale as OrderDetailPage's dropped back-link, CEO 2026-07-25;
          ProjectDetailPage.tsx chrome). */}
      {!nested && (
        <Link
          to={ctx ? `/projects/${ctx.projectId}/pos` : "/production/report"}
          className="mb-4 inline-flex min-h-[40px] items-center gap-1 text-[11px] font-medium uppercase tracking-[0.12em] text-muted hover:text-ink"
        >
          <ArrowLeft className="h-3 w-3" /> {ctx ? ctx.projectName : "Production"}
        </Link>
      )}

      {/* Record header (other UI handoff): number + status + facts row + ONE
          dominant action. */}
      <header className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            {/* Client name in the eyebrow only standalone — nested, the
                project chrome already shows it. */}
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              {nested ? (
                "Purchase order"
              ) : (
                <>Purchase order{ctx ? ` · ${ctx.clientName}` : ""}</>
              )}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
                PO #{po.poNumber}
              </h1>
              <StatusPill tone={poStageTone(po.internalStage)}>
                {poStageLabel(po.internalStage)}
              </StatusPill>
              {isFlagged && (
                <span className="inline-flex items-center gap-1 rounded bg-warn-bg px-2 py-1 text-[11px] font-medium text-warn">
                  <AlertCircle className="h-3.5 w-3.5" /> Exception
                </span>
              )}
              {po.followUpFlag === true && (
                <span className="inline-flex items-center gap-1 rounded bg-[#f1f1ee] px-2 py-1 text-[11px] font-medium text-ink">
                  <Flag className="h-3.5 w-3.5" /> Follow-up
                </span>
              )}
              {/* Creation-time gate overrides — quiet labels so a later viewer
                  sees the PO was minted with a check deliberately skipped. */}
              {data.created_flags?.skip_artwork_check === true && (
                <span
                  className="inline-flex items-center gap-1 rounded bg-[#f1f1ee] px-2 py-1 text-[11px] font-medium text-ink"
                  title="The rep chose to create this PO without every product line carrying artwork."
                >
                  <ImageOff className="h-3.5 w-3.5" /> Created without full artwork
                </span>
              )}
              {data.created_flags?.skip_zero_cost_check === true && (
                <span
                  className="inline-flex items-center gap-1 rounded bg-[#f1f1ee] px-2 py-1 text-[11px] font-medium text-ink"
                  title="The rep acknowledged $0-cost product lines when creating this PO."
                >
                  <CircleDollarSign className="h-3.5 w-3.5" /> $0 cost acknowledged
                </span>
              )}
            </div>
            <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
              <HeaderFact label={isDecoration ? "Decorator" : "Supplier"}>{ctx?.vendorName ?? "—"}</HeaderFact>
              {ctx && (
                <HeaderFact label="Order">
                  #{ctx.orderNumber}
                  {/* client · project — dropped when nested (the project chrome
                      already shows it); kept standalone. */}
                  {!nested && <span className="font-normal text-muted"> · {ctx.projectName}</span>}
                </HeaderFact>
              )}
              <HeaderFact label="Ship by">{formatDate(po.requiredShipDate)}</HeaderFact>
              <HeaderFact label="In hands">{formatDate(ctx?.inHandsDate ?? null)}</HeaderFact>
              <HeaderFact label="Cost">
                {/* A decorator PO's total is its decoration charges at our cost
                    (services/po-money.ts); $0 means the quote has none yet. */}
                {isDecoration && Number(po.subtotalCost) <= 0 ? "Not yet priced" : formatMoney(po.subtotalCost)}
              </HeaderFact>
            </dl>
          </div>

          {/* Actions — one dominant (ink) step for the PO's stage; everything
              else is an outline secondary. Mutations gated on canManage
              (admin / production_rep, matching the route roles). */}
          {canManage && (
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              {isDraft && (
                <button
                  type="button"
                  onClick={() => {
                    setBanner(null);
                    setDecoConfirmPending(false);
                    setSendModalError(null);
                    setSendModalNotice(null);
                    setShowSendModal(true);
                  }}
                  disabled={sendMutation.isPending || blockSend.length > 0}
                  title={
                    blockSend.length > 0
                      ? `Add the ${joinMissing(blockSend)} first.`
                      : undefined
                  }
                  className={primaryBtn}
                >
                  <Send className="h-4 w-4" />
                  {sendMutation.isPending ? "Sending…" : "Send to supplier"}
                </button>
              )}

              {/* Preview PO, like Preview Quote (Stephanie 2026-10-27): the
                  supplier's own page in a new tab. A draft opens in staff
                  preview mode, so nothing is sent or marked as seen. */}
              {po.internalStage !== "cancelled" && (
                <button
                  type="button"
                  onClick={() => window.open(`/po/${po.publicShareUuid}`, "_blank", "noopener,noreferrer")}
                  title="Open the PO as the supplier will see it, in a new tab. Use Download as PDF there."
                  className={outlineBtn}
                >
                  <ExternalLink className="h-4 w-4" /> Preview PO
                </button>
              )}

              {/* Resend (Stephanie 2026-10-14): email the PO again after it's
                  gone out — e.g. after a change. Same checks; stage unchanged. */}
              {canResend && (
                <button
                  type="button"
                  onClick={() => {
                    setBanner(null);
                    setDecoConfirmPending(false);
                    setSendModalError(null);
                    setSendModalNotice(null);
                    setShowSendModal(true);
                  }}
                  disabled={sendMutation.isPending || blockSend.length > 0}
                  title={
                    blockSend.length > 0
                      ? `Add the ${joinMissing(blockSend)} first.`
                      : "Email this PO to the supplier again"
                  }
                  className={outlineBtn}
                >
                  <Send className="h-4 w-4" /> Resend PO
                </button>
              )}
              {/* No "Start production" here — production is tracked on the
                  Production Report (Stephanie 2026-11-03). */}
              {po.internalStage === "delivered" && (
                <button
                  type="button"
                  onClick={() => billsReceivedMutation.mutate()}
                  disabled={billsReceivedMutation.isPending}
                  className={primaryBtn}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {billsReceivedMutation.isPending
                    ? "Marking…"
                    : "Mark bills received"}
                </button>
              )}
              {canCopyShareLink && (
                <button
                  type="button"
                  onClick={() => void copyShareLink(vendorShareUrl)}
                  title={
                    copied ? "Link copied to clipboard" : "Copy the supplier link"
                  }
                  className={outlineBtn}
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4" /> Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" /> Copy link
                    </>
                  )}
                </button>
              )}
              {!editing && (
                <button type="button" onClick={startEdit} className={outlineBtn} title="Edit the next action, ship date and internal notes (Production box)">
                  <Pencil className="h-4 w-4" /> Edit
                </button>
              )}
              {isFlagged ? (
                <button
                  type="button"
                  onClick={() => exceptionClear.mutate()}
                  disabled={exceptionClear.isPending}
                  className={outlineBtn}
                >
                  <X className="h-4 w-4" /> Clear exception
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setExMode((v) => !v);
                    setBanner(null);
                  }}
                  className={outlineBtn}
                >
                  <AlertCircle className="h-4 w-4" /> Flag exception
                </button>
              )}
              {/* E6 — destructive action, separated from the constructive ones. */}
              <button
                type="button"
                disabled={!canCancel}
                title={
                  !canCancel
                    ? "This PO is in a terminal stage (cancelled/closed) and can't be cancelled"
                    : undefined
                }
                onClick={() => {
                  setCancelMode((v) => !v);
                  setBanner(null);
                }}
                className="inline-flex h-10 items-center gap-1.5 rounded-md px-3 text-sm text-risk hover:bg-risk-bg disabled:cursor-not-allowed disabled:opacity-50"
              >
                <X className="h-4 w-4" /> Cancel PO
              </button>
            </div>
          )}
        </div>

        {/* The supplier's order + art emails, added or changed right here
            (Stephanie 2026-11-02: "add back the fields to add the order and art
            email"). Saved on the supplier, so every PO to them uses them. */}
        {vendorId && vendorData && (
          <SupplierEmails
            vendorId={vendorId}
            orderEmail={vendorData.vendor.primaryEmail ?? null}
            artEmail={vendorData.vendor.artEmail ?? null}
            canEdit={canManage}
          />
        )}
        {/* Why Send is greyed out (Stephanie 2026-10-14). Lives down here, not
            in the button row — a full-width line there squeezed the PO facts. */}
        {canManage && (isDraft || canResend) && blockSend.length > 0 && (
          <p className="mt-2 inline-flex items-center gap-1.5 rounded bg-warn-bg px-2.5 py-1.5 text-xs font-medium text-warn">
            <AlertCircle className="h-3.5 w-3.5" />
            Add the {joinMissing(blockSend)} before sending.
          </p>
        )}
      </header>

      {/* D4 — two-leg chain (blanks + decorator): the reciprocal link to the
          paired PO, shown on BOTH sides, plus the blank-arrival state on the
          decoration side (handoff W15). */}
      {ctx?.pairedPoId &&
        ctx.pairedPoNumber != null &&
        po.internalStage !== "cancelled" && (
        <section
          aria-label="Linked purchase order"
          className={`mb-6 flex flex-wrap items-center justify-between gap-3 rounded-md border px-[18px] py-4 ${
            blanksPending ? "border-warn/30 bg-warn-bg" : "border-rule bg-white"
          }`}
        >
          <div className="min-w-0">
            <p className={`text-sm font-medium ${blanksPending ? "text-warn" : "text-ink"}`}>
              {isDecoration
                ? blanksPending
                  ? "Blanks have not arrived at the decorator yet."
                  : blanksHere
                    ? "Blanks have arrived at the decorator."
                    : "Blanks are on a linked purchase order."
                : "These goods ship to a decorator."}
            </p>
            <p className="mt-0.5 text-xs text-muted">
              {isDecoration
                ? `PO #${ctx.pairedPoNumber} (blank goods${ctx.shipFromVendorName ? ` from ${ctx.shipFromVendorName}` : ""}) is ${poStageLabel(ctx.pairedPoStage ?? "").toLowerCase() || "linked"}. Arrival is confirmed when that PO is marked delivered.`
                : `Decoration is on PO #${ctx.pairedPoNumber}. Mark this PO delivered when the decorator confirms the blanks arrived.`}
            </p>
          </div>
          <Link
            to={`/projects/${ctx.projectId}/pos/${ctx.pairedPoId}`}
            className={outlineBtn}
          >
            {po.poType === "decoration_service"
              ? `Blanks come from PO #${ctx.pairedPoNumber}`
              : `Decoration on PO #${ctx.pairedPoNumber}`}{" "}
            →
          </Link>
        </section>
      )}

      {/* D2 — the $0-decoration soft gate is now handled INSIDE the send dialog
          (decoConfirmPending + an in-modal notice): the rep confirms with a
          second "Send PO" click, keeping their recipients + message intact. */}

      {/* D6 — Cancel PO reason capture (the server requires a reason). */}
      {cancelMode && (
        <div className="mb-6 rounded-md border border-risk/30 bg-risk-bg px-[18px] py-4 text-sm text-ink">
          <p className="font-medium text-risk">Cancel this purchase order</p>
          <p className="mt-1 text-muted">
            Give a reason (recorded on the PO's history). A cancelled PO frees its
            lines so they can be re-created.
          </p>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            rows={2}
            placeholder="Reason for cancelling…"
            aria-label="Reason for cancelling"
            className="mt-2 block w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              variant="risk"
              disabled={cancelMutation.isPending || cancelReason.trim().length === 0}
              onClick={() => cancelMutation.mutate(cancelReason.trim())}
            >
              {cancelMutation.isPending ? "Cancelling…" : "Cancel PO"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCancelMode(false);
                setCancelReason("");
              }}
            >
              Keep PO
            </Button>
          </div>
        </div>
      )}

      {/* Banner */}
      {banner && (
        <div
          role={banner.kind === "err" ? "alert" : "status"}
          className={`mb-6 rounded-md border px-[18px] py-3 text-sm ${
            banner.kind === "ok"
              ? "border-ok/20 bg-ok-bg text-ok"
              : banner.kind === "warn"
                ? "border-warn/30 bg-warn-bg font-medium text-warn"
                : "border-risk/30 bg-risk-bg text-risk"
          }`}
        >
          {banner.msg}
        </div>
      )}

      {/* G2 — a cancelled PO frees its lines to re-create. This forward link is
          gated PURELY on the PO's cancelled stage (NOT nested in the transient
          post-action banner), so it persists across a fresh page load of an
          already-cancelled PO. */}
      {po.internalStage === "cancelled" && (
        <div className="mb-6 rounded-md border border-rule bg-white px-[18px] py-3 text-sm text-ink">
          This PO is cancelled — its lines are free to re-create.{" "}
          <Link
            to={
              ctx
                ? `/projects/${ctx.projectId}/quotes/${po.orderId}`
                : `/orders/${po.orderId}`
            }
            className="font-medium text-ink underline underline-offset-4 hover:text-muted"
          >
            Go to the order to re-create →
          </Link>
        </div>
      )}

      {/* Zero-cost product-line warning — caught on a DRAFT before "Send to
          supplier" is ever clicked. */}
      {isDraft && zeroCostLines.length > 0 && (
        <div className="mb-6 rounded-md border border-warn/30 bg-warn-bg px-[18px] py-3 text-sm font-medium text-warn">
          <span className="inline-flex items-center gap-1.5">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>
              {zeroCostLines.length} product line
              {zeroCostLines.length === 1 ? "" : "s"} priced at $0 — the supplier
              would read this as free goods. Fix the unit cost before sending:{" "}
              {zeroCostLines.join(", ")}.
            </span>
          </span>
        </div>
      )}

      {/* Pricing sanity — per-line cost vs canonical supplier cost + vs sell
          price. Warn tone, NEVER blocking. Caught on a DRAFT before "Send to
          supplier" so a wrong cost never reaches the supplier. Staff-only —
          this canon comparison is never on the public /po/:uuid vendor view. */}
      {isDraft && pricingWarnings.length > 0 && (
        <div className="mb-6 rounded-md border border-warn/30 bg-warn-bg px-[18px] py-3 text-sm text-warn">
          <p className="mb-2 inline-flex items-center gap-1.5 font-medium">
            <AlertCircle className="h-4 w-4" />
            Pricing check — {pricingWarnings.length} line
            {pricingWarnings.length === 1 ? "" : "s"} to review before sending
          </p>
          <ul className="space-y-1 text-ink">
            {pricingWarnings.map((w, i) => (
              <li key={`${w.lineItemId}-${w.kind}-${i}`}>
                {w.kind === "cost_at_or_above_sell" ? (
                  <span>
                    {w.name}: cost {formatMoney(String(w.lineCost))} ≥ sell
                    price {formatMoney(String(w.sellPrice))} — check for swapped
                    fields
                  </span>
                ) : (
                  <span>
                    {w.name}: cost {formatMoney(String(w.lineCost))} vs supplier{" "}
                    {formatMoney(
                      w.canonicalCost === null ? null : String(w.canonicalCost),
                    )}
                    {w.kind === "cost_above_canon" ? " (over)" : " (under)"}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Exception form */}
      {exMode && canManage && !isFlagged && (
        <div className="mb-6 rounded-md border border-rule bg-white p-[18px]">
          <p className="mb-3 text-sm font-medium text-ink">Flag an exception</p>
          <div className="grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-end">
            <label className="text-xs text-muted">
              Reason
              <select
                value={exReason}
                onChange={(e) => setExReason(e.target.value)}
                className="mt-1 block h-10 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink"
              >
                {EXCEPTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs text-muted">
              Note {exReason === "other" && <span className="text-risk">(required)</span>}
              <input
                type="text"
                value={exNote}
                onChange={(e) => setExNote(e.target.value)}
                placeholder="What happened?"
                className="mt-1 block h-10 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink"
              />
            </label>
            <button
              type="button"
              disabled={
                exceptionSet.isPending ||
                (exReason === "other" && exNote.trim().length === 0)
              }
              onClick={() =>
                exceptionSet.mutate({
                  reason: exReason,
                  note: exNote.trim() || undefined,
                })
              }
              className={primaryBtn}
            >
              Flag
            </button>
          </div>
        </div>
      )}

      {/* Shipping instructions — the vendor-facing "how / where / when to ship"
          editor. Placed above the fields grid (and the header Send action is
          right there) so the natural flow is configure-shipping → send. Owns
          ship-to (incl. vendor/decorator destination), ship-via (+ client
          carrier account), the required ship date, and vendor-visible notes. */}
      <PoShippingSection
        poId={id ?? ""}
        po={{
          shippingAddressSnapshot: po.shippingAddressSnapshot,
          shippingAddressId: po.shippingAddressId,
          shippingAccountId: po.shippingAccountId,
          shippingMethod: po.shippingMethod,
          requiredShipDate: po.requiredShipDate,
          notes: po.notes,
          shipFromVendorId: po.shipFromVendorId,
          poType: po.poType,
          shippingQuote: data.po.shippingQuote ?? null,
          displayShipTo: data.vendor_ship_to ?? null,
        }}
        shippingQuoteReady={data.shipping_quote_ready === true}
        ctx={
          ctx
            ? {
                clientId: ctx.clientId,
                vendorName: ctx.vendorName,
                inHandsDate: ctx.inHandsDate,
                shipFromVendorName: ctx.shipFromVendorName,
              }
            : null
        }
        canManage={canManage}
        onSaved={invalidate}
      />

      {/* One full-width column (Stephanie 2026-10-27: "the item details
          should be centered and take up the width"); Production moved to the
          bottom as a full-width card. */}
      <div className="space-y-6">
        <div className="space-y-6">
          {/* The product lines are already in Item details below, so this box
              only shows what that doesn't: charges that aren't products, or a
              decoration PO's explanation (Stephanie 2026-10-27: "remove the
              line items box. its redundant"). */}
          {(lines.length === 0 || otherLines.length > 0) && (
          <section className={card} aria-labelledby="po-lines">
            <div className="flex flex-wrap items-baseline justify-between gap-2 px-[22px] pb-3 pt-5">
              <h2 id="po-lines" className={cardTitle}>{lines.length === 0 ? "Line items" : "Other charges"}</h2>
              <span className="text-sm font-medium tabular-nums text-ink">
                {lines.length === 0
                  ? formatMoney(po.subtotalCost)
                  : formatMoney(String(otherLines.reduce((n, l) => n + Number(l.totalCost ?? 0), 0)))}
                {isDecoration && Number(po.subtotalCost) <= 0 && (
                  <span className="ml-2 font-normal text-muted">
                    (no decoration charges on the quote yet)
                  </span>
                )}
              </span>
            </div>
            {lines.length === 0 ? (
              <div className="border-t border-rule p-8 text-center text-sm text-muted">
                {po.poType === "decoration_service" ? (
                  // D4 — a decoration PO is header-only by design (the blanks
                  // live on its paired supplier PO). This isn't an error state.
                  <>
                    This PO is the decoration work on the items from
                    {ctx?.pairedPoNumber != null
                      ? ` the paired supplier PO #${ctx.pairedPoNumber}`
                      : " the paired supplier PO"}
                    . Its total is the decoration charges (setup, run…) at our cost.
                  </>
                ) : (
                  "No line items on this PO."
                )}
              </div>
            ) : (
              <div className="overflow-x-auto border-t border-rule">
                <table className="w-full text-left">
                  <thead className={TABLE_HEAD}>
                    <tr>
                      <th scope="col" className={TH}>Item</th>
                      <th scope="col" className={`${TH} text-right`}>Qty</th>
                      <th scope="col" className={`${TH} hidden text-right sm:table-cell`}>Unit cost</th>
                      <th scope="col" className={`${TH} text-right`}>Total</th>
                    </tr>
                  </thead>
                  <tbody className="text-[13px]">
                    {otherLines.map((l) => (
                      <tr key={l.id} className="border-b border-[#e7e7e4] last:border-0">
                        <td className={`${TD} text-ink`}>
                          {l.name}
                          {l.serviceType !== "product" && (
                            <span className="ml-2 text-xs text-muted">
                              {labelize(l.serviceType)}
                            </span>
                          )}
                        </td>
                        <td className={`${TD} text-right tabular-nums text-ink`}>
                          {l.quantity}
                        </td>
                        <td className={`${TD} hidden text-right tabular-nums text-muted sm:table-cell`}>
                          {formatMoney(l.unitCost)}
                        </td>
                        <td className={`${TD} text-right font-medium tabular-nums text-ink`}>
                          {formatMoney(l.totalCost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          )}

          {/* Item details — SKU + variants + decoration + artwork, exactly
              what the vendor sees on /po/:uuid. Staff parity so nothing is a
              surprise when the supplier calls. */}
          {vendorLines.some((l) => l.serviceType === "product") && (
            <section className={`${card} p-[22px]`} aria-labelledby="po-items">
              <h2 id="po-items" className={cardTitle}>
                Item details
              </h2>
              <p className="mb-4 mt-1 text-sm text-muted">
                Variants, decoration, and artwork — what the supplier sees on the PO.
              </p>
              <div className="space-y-4">
                {vendorLines
                  .filter((l) => l.serviceType === "product")
                  .map((l) => (
                    <div
                      key={l.id}
                      className="rounded-md border border-[#e7e7e4] p-4"
                    >
                      <div className="flex gap-4">
                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded border border-[#e7e7e4] bg-canvas">
                          {l.imageUrl ? (
                            <img
                              src={l.imageUrl}
                              alt={l.name}
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-muted">
                              <ImageOff className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          {l.sku && (
                            <p className="text-[11px] text-muted">
                              SKU {l.sku}
                            </p>
                          )}
                          <p className="font-medium text-ink">{l.name}</p>
                          {l.description && l.description !== l.name && (
                            <p className="mt-0.5 text-sm text-muted">
                              {l.description}
                            </p>
                          )}
                        </div>
                        {canManage && canRemoveLines && removing?.id !== l.id && (
                          <button
                            type="button"
                            onClick={() => setRemoving({ id: l.id, name: l.name, reason: "Out of stock" })}
                            className="h-fit shrink-0 text-sm font-medium text-rust-600 hover:underline"
                          >
                            Remove from PO
                          </button>
                        )}
                      </div>
                      {removing?.id === l.id && (
                        <form
                          className="mt-3 rounded-md border border-rust-200 bg-rust-50 p-3"
                          onSubmit={(e) => {
                            e.preventDefault();
                            removeLineMutation.mutate(removing);
                          }}
                        >
                          <p className="text-sm font-medium text-ink">Remove {l.name} from this PO?</p>
                          <p className="mt-0.5 text-sm text-muted">
                            Only the PO changes. The quote and invoice keep it so the sales rep can replace it.
                          </p>
                          <label className="mt-2 block text-sm text-ink">
                            Reason (saved in the PO notes)
                            <input
                              value={removing.reason}
                              onChange={(e) => setRemoving({ ...removing, reason: e.target.value })}
                              maxLength={300}
                              className="mt-1 block h-9 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink"
                            />
                          </label>
                          <div className="mt-3 flex gap-2">
                            <Button type="submit" disabled={removeLineMutation.isPending}>
                              {removeLineMutation.isPending ? "Removing…" : "Remove from PO"}
                            </Button>
                            <Button type="button" variant="ghost" onClick={() => setRemoving(null)} disabled={removeLineMutation.isPending}>
                              Keep it
                            </Button>
                          </div>
                        </form>
                      )}

                      {l.variants.length > 0 && (
                        <table className="mt-3 w-full text-sm">
                          <thead>
                            <tr className="text-left text-[11px] font-medium text-muted">
                              <th className="py-1 font-medium">Variant</th>
                              <th className="py-1 text-right font-medium">Qty</th>
                              <th className="py-1 text-right font-medium">Unit cost</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#efefec]">
                            {l.variants.map((v) => (
                              <tr key={v.id}>
                                <td className="py-1.5 text-ink">
                                  {[v.color, v.size, v.material]
                                    .filter(Boolean)
                                    .join(" · ") || "Standard"}
                                </td>
                                <td className="py-1.5 text-right tabular-nums text-ink">
                                  {v.quantity}
                                </td>
                                <td className="py-1.5 text-right tabular-nums text-muted">
                                  {formatMoney(v.unitCost ?? l.unitCost)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      <SplitShipTo
                        splits={data.ship_splits?.[l.lineItemId]?.map((x) => ({ address: x.addressSnapshot, quantities: x.quantities, quantity: x.quantity }))}
                      />

                      {l.decorations.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className="text-[11px] font-medium text-muted">
                            Decoration
                          </p>
                          {l.decorations.map((d) => (
                            <StaffDecoration key={d.id} decoration={d} />
                          ))}
                        </div>
                      )}

                      {l.artwork.length > 0 && (
                        <div className="mt-3">
                          <p className="mb-1.5 text-[11px] font-medium text-muted">
                            Artwork
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {l.artwork.map((a) => (
                              <StaffArtworkLink key={a.fileId} artwork={a} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </section>
          )}

          {/* Tracking — rep-facing rename of the Shipments surface (CEO
              2026-07-24). API routes/types/schema stay `shipments`; only the
              labels reps read change to "Tracking" so the section is not
              mistaken for a second ship-to ADDRESS entry. */}
          <section className={card} aria-labelledby="po-tracking">
            <div className="flex flex-wrap items-center justify-between gap-2 px-[22px] pb-3 pt-5">
              <h2 id="po-tracking" className={cardTitle}>
                Tracking
              </h2>
              {canManage && (
                <button
                  type="button"
                  onClick={() => {
                    setFillingId(null);
                    setShowShipForm((v) => !v);
                    setBanner(null);
                  }}
                  className={outlineBtn}
                >
                  <Truck className="h-4 w-4" /> Add tracking
                </button>
              )}
            </div>

            {showShipForm && canManage && (
              <div className="border-t border-rule px-[22px] py-4">
                {fillingRow ? (
                  <p className="mb-3 text-sm text-ink">
                    Adding tracking for the shipment still to come to <span className="font-medium">{placeLabel(fillingRow.destinationAddressSnapshot)}</span>.
                  </p>
                ) : null}
                {po.shippingAddressId || shipDests.length > 0 ? (
                  <>
                  {!fillingRow && shipDests.length > 1 && (
                    <label className="mb-3 block text-xs text-muted">
                      Ship to (this item is split)
                      <select
                        value={shipDest}
                        onChange={(e) => setShipDest(e.target.value)}
                        className="mt-1 block h-10 w-full max-w-md rounded-md border border-rule bg-white px-3 text-sm text-ink"
                      >
                        <option value="">Pick where this box went</option>
                        {shipDests.map((d) => (
                          <option key={d.id} value={d.id}>{d.label}</option>
                        ))}
                      </select>
                    </label>
                  )}
                  <div className="grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
                    <label className="text-xs text-muted">
                      Carrier
                      <select
                        value={shipCarrier}
                        onChange={(e) => setShipCarrier(e.target.value)}
                        className="mt-1 block h-10 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink"
                      >
                        {CARRIER_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs text-muted">
                      Tracking #
                      <input
                        type="text"
                        value={shipTracking}
                        onChange={(e) => setShipTracking(e.target.value)}
                        placeholder="1Z…"
                        className="mt-1 block h-10 w-full rounded-md border border-rule px-3 text-sm text-ink"
                      />
                    </label>
                    <label className="text-xs text-muted">
                      Tracking URL (optional)
                      <input
                        type="url"
                        value={shipTrackingUrl}
                        onChange={(e) => setShipTrackingUrl(e.target.value)}
                        placeholder="https://…"
                        className="mt-1 block h-10 w-full rounded-md border border-rule px-3 text-sm text-ink"
                      />
                    </label>
                    <button
                      type="button"
                      disabled={
                        createShipmentMutation.isPending ||
                        fillShipmentMutation.isPending ||
                        (!fillingRow && shipDests.length > 1 && shipDest === "")
                      }
                      title={!fillingRow && shipDests.length > 1 && shipDest === "" ? "Pick where this box went" : undefined}
                      onClick={() => {
                        const qty = Number(shipQty);
                        const boxes = Number(shipBoxes);
                        const common = {
                          ...(Number.isInteger(boxes) && boxes > 1 ? { box_count: boxes } : {}),
                          carrier: shipCarrier,
                          tracking_number: shipTracking.trim() || null,
                          tracking_url: shipTrackingUrl.trim() || null,
                          ...(Number.isInteger(qty) && qty > 0 ? { shipped_quantity: qty } : {}),
                          ...(shipMore ? { more_to_come: true } : {}),
                        };
                        if (fillingRow) fillShipmentMutation.mutate({ shipmentId: fillingRow.id, ...common });
                        else
                          createShipmentMutation.mutate({
                            destination_address_id: shipDests.length > 1 ? shipDest : shipDests[0]?.id ?? po.shippingAddressId!,
                            ...common,
                          });
                      }}
                      className={primaryBtn}
                    >
                      {createShipmentMutation.isPending || fillShipmentMutation.isPending ? "Saving…" : "Record"}
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-4">
                    <label className="text-xs text-muted">
                      Pieces in this shipment (optional)
                      <input
                        type="number"
                        min={1}
                        inputMode="numeric"
                        value={shipQty}
                        onChange={(e) => setShipQty(e.target.value)}
                        className="mt-1 block h-10 w-32 rounded-md border border-rule px-3 text-sm text-ink"
                      />
                    </label>
                    <label className="text-xs text-muted">
                      Boxes under this tracking #
                      <input
                        type="number"
                        min={1}
                        inputMode="numeric"
                        placeholder="1"
                        value={shipBoxes}
                        onChange={(e) => setShipBoxes(e.target.value)}
                        className="mt-1 block h-10 w-28 rounded-md border border-rule px-3 text-sm text-ink"
                      />
                    </label>
                    <label className="flex items-center gap-2 pt-4 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={shipMore}
                        onChange={(e) => setShipMore(e.target.checked)}
                        className="h-4 w-4 rounded border-rule"
                      />
                      More shipments to come for this PO
                    </label>
                  </div>
                  {shipMore && (
                    <p className="mt-2 text-[12px] text-muted">
                      Adds a “tracking to come” line, so the PO isn’t marked delivered — and the invoice due date doesn’t start — until the rest lands.
                    </p>
                  )}
                  </>
                ) : (
                  <p className="text-sm text-muted">
                    This PO has no shipping address, so tracking can't be added
                    yet.
                  </p>
                )}
              </div>
            )}

            {(shipData?.rows.length ?? 0) === 0 ? (
              <div className="border-t border-rule p-8 text-center text-sm text-muted">
                Add tracking when the supplier ships.
              </div>
            ) : (
              <div className="overflow-x-auto border-t border-rule">
                <table className="w-full text-left">
                  <thead className={TABLE_HEAD}>
                    <tr>
                      <th scope="col" className={TH}>Carrier</th>
                      <th scope="col" className={TH}>Tracking</th>
                      <th scope="col" className={`${TH} hidden md:table-cell`}>Ship to</th>
                      <th scope="col" className={`${TH} hidden md:table-cell text-right`}>Pieces</th>
                      <th scope="col" className={`${TH} hidden md:table-cell`}>Boxes</th>
                      <th scope="col" className={TH}>Status</th>
                      <th scope="col" className={`${TH} hidden sm:table-cell`}>Shipped</th>
                      <th scope="col" className={TH}><span className="sr-only">Action</span></th>
                    </tr>
                  </thead>
                  <tbody className="text-[13px]">
                    {(shipData?.rows ?? []).map((s) => (
                      <Fragment key={s.id}>
                      <tr className="border-b border-[#e7e7e4] last:border-0">
                        <td className={`${TD} capitalize text-ink`}>
                          {s.carrier ? labelize(s.carrier) : "—"}
                        </td>
                        <td className={`${TD} text-ink`}>
                          {s.trackingNumber ? (
                            s.trackingUrl ? (
                              <a
                                href={s.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline underline-offset-2 hover:text-muted"
                              >
                                {s.trackingNumber}
                              </a>
                            ) : (
                              s.trackingNumber
                            )
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className={`${TD} hidden text-ink md:table-cell`}>{placeLabel(s.destinationAddressSnapshot)}</td>
                        <td className={`${TD} hidden text-right tabular-nums text-ink md:table-cell`}>{s.shippedQuantity ?? "—"}</td>
                        <td className={`${TD} hidden text-ink md:table-cell`}>{boxesText(s.boxCount, s.boxesDelivered, s.status)}</td>
                        <td className={TD}>
                          {s.status === "pending" && !s.trackingNumber ? (
                            <span className="text-xs font-semibold text-warn">Tracking to come</span>
                          ) : (
                            <StatusPill tone={shipmentTone(s.status)}>
                              {labelize(s.status)}
                            </StatusPill>
                          )}
                        </td>
                        <td className={`${TD} hidden text-muted sm:table-cell`}>
                          {s.shippedAt
                            ? new Date(s.shippedAt).toLocaleDateString()
                            : "—"}
                        </td>
                        <td className={`${TD} text-right`}>
                          {canManage && s.status === "pending" && !s.trackingNumber && (
                            <span className="inline-flex gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setFillingId(s.id);
                                  setShipMore(false);
                                  setShowShipForm(true);
                                  setBanner(null);
                                }}
                                className={outlineBtn}
                              >
                                Add tracking
                              </button>
                              <button
                                type="button"
                                disabled={removePlaceholderMutation.isPending}
                                onClick={() => removePlaceholderMutation.mutate(s.id)}
                                title="Nothing more is coming — remove this line"
                                className={outlineBtn}
                              >
                                Not needed
                              </button>
                            </span>
                          )}
                          {canManage &&
                            (s.status === "in_transit" ||
                              s.status === "out_for_delivery") && (
                              <button
                                type="button"
                                disabled={shipTransitionMutation.isPending}
                                onClick={() =>
                                  shipTransitionMutation.mutate({
                                    shipmentId: s.id,
                                    event: "deliver",
                                  })
                                }
                                className={outlineBtn}
                              >
                                Mark delivered
                              </button>
                            )}
                          {s.status === "delivered" && (
                            <span className="text-xs text-muted">
                              Delivered
                              {s.deliveredAt
                                ? ` ${new Date(s.deliveredAt).toLocaleDateString()}`
                                : ""}
                            </span>
                          )}
                          {canManage && !!s.trackingNumber && (
                            <span className="ml-2 inline-flex gap-2 align-middle">
                              <button
                                type="button"
                                onClick={() => setEditShip({ id: s.id, carrier: s.carrier ?? "", tracking: s.trackingNumber ?? "", url: s.trackingUrl ?? "", boxes: s.boxCount ? String(s.boxCount) : "" })}
                                className="text-xs font-medium text-muted hover:text-ink"
                              >
                                Edit
                              </button>
                              {confirmDeleteShip === s.id ? (
                                <span className="inline-flex gap-2 text-xs">
                                  Delete?
                                  <button type="button" disabled={deleteShipMutation.isPending} onClick={() => deleteShipMutation.mutate(s.id)} className="font-semibold text-rust-600 hover:underline">
                                    Yes
                                  </button>
                                  <button type="button" onClick={() => setConfirmDeleteShip(null)} className="text-muted hover:text-ink">No</button>
                                </span>
                              ) : (
                                <button type="button" onClick={() => setConfirmDeleteShip(s.id)} className="text-xs font-medium text-muted hover:text-ink">
                                  Delete
                                </button>
                              )}
                            </span>
                          )}
                        </td>
                      </tr>
                      {editShip?.id === s.id && (
                        <tr className="border-b border-[#e7e7e4] bg-cream-50">
                          <td colSpan={8} className="px-4 py-3">
                            <form
                              className="flex flex-wrap items-end gap-3"
                              onSubmit={(e) => {
                                e.preventDefault();
                                editShipMutation.mutate(editShip);
                              }}
                            >
                              <label className="text-xs text-muted">
                                Carrier
                                <select
                                  value={editShip.carrier}
                                  onChange={(e) => setEditShip({ ...editShip, carrier: e.target.value })}
                                  className="mt-1 block h-9 rounded-md border border-rule bg-white px-2 text-sm text-ink"
                                >
                                  {CARRIER_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                  ))}
                                </select>
                              </label>
                              <label className="text-xs text-muted">
                                Tracking #
                                <input
                                  value={editShip.tracking}
                                  onChange={(e) => setEditShip({ ...editShip, tracking: e.target.value })}
                                  className="mt-1 block h-9 w-56 rounded-md border border-rule bg-white px-2 text-sm text-ink"
                                />
                              </label>
                              <label className="text-xs text-muted">
                                Boxes
                                <input
                                  type="number"
                                  min={1}
                                  value={editShip.boxes}
                                  onChange={(e) => setEditShip({ ...editShip, boxes: e.target.value })}
                                  className="mt-1 block h-9 w-20 rounded-md border border-rule bg-white px-2 text-sm text-ink"
                                />
                              </label>
                              <label className="min-w-[220px] flex-1 text-xs text-muted">
                                Tracking link (optional)
                                <input
                                  value={editShip.url}
                                  onChange={(e) => setEditShip({ ...editShip, url: e.target.value })}
                                  placeholder="https://…"
                                  className="mt-1 block h-9 w-full rounded-md border border-rule bg-white px-2 text-sm text-ink"
                                />
                              </label>
                              <button type="submit" disabled={editShipMutation.isPending} className="h-9 rounded-md bg-ink px-3 text-sm font-medium text-white disabled:opacity-50">
                                {editShipMutation.isPending ? "Saving…" : "Save"}
                              </button>
                              <button type="button" onClick={() => setEditShip(null)} className="h-9 px-2 text-sm text-ink">Cancel</button>
                            </form>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className={card} aria-labelledby="po-notes">
            <h2 id="po-notes" className={`${cardTitle} px-[22px] pb-3 pt-5`}>
              Notes
            </h2>
            <div className="border-t border-rule px-[22px] py-4">
              <MentionTextarea
                value={noteText}
                onChange={(t, ids) => { setNoteText(t); setNoteMentions(ids); }}
                placeholder="Add a note (visible to the whole team)… type @ to tag someone"
                aria-label="New note"
                rows={2}
                className="block w-full rounded-md border border-rule px-3 py-2 text-sm text-ink"
              />
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  disabled={addNote.isPending || noteText.trim().length === 0}
                  onClick={() => addNote.mutate(noteText.trim())}
                  className={outlineBtn}
                >
                  {addNote.isPending ? "Adding…" : "Add note"}
                </button>
              </div>
            </div>
            {notes.length === 0 ? (
              <p className="border-t border-rule p-5 text-center text-sm text-muted">
                No notes yet.
              </p>
            ) : (
              <ul className="divide-y divide-[#e7e7e4] border-t border-rule">
                {notes.map((n) => (
                  <li key={n.id} className="px-[22px] py-4">
                    {/* Edit / Delete (Stephanie 2026-10-30). */}
                    <EditDeleteNote
                      fields={[{ key: "body", label: "Note", value: n.body }]}
                      onSave={async (v) => {
                        await api(`/pos/${id}/notes/${n.id}`, { method: "PATCH", body: JSON.stringify({ body: (v.body ?? "").trim() }) });
                        await queryClient.invalidateQueries({ queryKey: detailKey });
                      }}
                      onDelete={async () => {
                        await api(`/pos/${id}/notes/${n.id}`, { method: "DELETE" });
                        await queryClient.invalidateQueries({ queryKey: detailKey });
                      }}
                    >
                      <p className="whitespace-pre-line text-sm text-ink">
                        <NoteText text={n.body} />
                      </p>
                      <p className="mt-1 text-xs text-muted">
                        {n.createdAt
                          ? new Date(n.createdAt).toLocaleString()
                          : ""}
                        {n.isPinned ? " · pinned" : ""}
                      </p>
                    </EditDeleteNote>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {id && <SentEmailsCard doc="po" id={id} className={card} />}
        </div>

        <section className={`${card} p-[22px]`} aria-labelledby="po-production">
          <h2 id="po-production" className={`${cardTitle} mb-4`}>Production</h2>
          {editing && canManage ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <EditSelect
                label="Next action"
                value={(form.next_action_type as string) ?? ""}
                onChange={(v) => setForm((f) => ({ ...f, next_action_type: v }))}
                options={NEXT_ACTION_OPTIONS}
                placeholder="None"
              />
              <EditInput
                label="Next action date"
                type="date"
                value={(form.next_action_date as string) ?? ""}
                onChange={(v) => setForm((f) => ({ ...f, next_action_date: v }))}
              />
              <EditInput
                label="Supplier ship date"
                type="date"
                value={(form.supplier_ship_date as string) ?? ""}
                onChange={(v) =>
                  setForm((f) => ({ ...f, supplier_ship_date: v }))
                }
              />
              {/* Ship-via / ship-by / ship-to / vendor-notes are owned solely by
                  PoShippingSection (carrier + service compose into shipping_method
                  server-side). No raw shipping_method input here — a free-text
                  edit would bypass that composition and reset the carrier. */}
              <EditInput
                label="Internal notes"
                value={(form.internal_notes as string) ?? ""}
                onChange={(v) => setForm((f) => ({ ...f, internal_notes: v }))}
              />
              <label className="flex min-h-[40px] items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={Boolean(form.follow_up_flag)}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, follow_up_flag: e.target.checked }))
                  }
                  className="h-4 w-4 accent-[#080808]"
                />
                Follow-up flag
              </label>
              <div className="col-span-full flex gap-2 pt-1">
                <button
                  type="button"
                  onClick={saveEdit}
                  disabled={patchMutation.isPending}
                  className={primaryBtn}
                >
                  {patchMutation.isPending ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className={outlineBtn}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Next action" value={po.nextActionType ? (NEXT_ACTION_OPTIONS.find((o) => o.value === po.nextActionType)?.label ?? labelize(po.nextActionType)) : "None"} />
              <Field
                label="Next action date"
                value={formatDate(po.nextActionDate)}
              />
              <Field
                label="Supplier ship date"
                value={formatDate(po.supplierShipDate)}
              />
              {/* Required ship date + shipping method now live in the Shipping
                  section above — not duplicated here. */}
              {isFlagged && (
                <Field
                  label="Exception reason"
                  value={EXCEPTION_OPTIONS.find((o) => o.value === po.exceptionReason)?.label ?? labelize(po.exceptionReason)}
                />
              )}
              <div className="col-span-full"><Field label="Internal notes" value={po.internalNotes ?? "—"} wrap /></div>
            </dl>
          )}
        </section>
      </div>

      {/* Send-to-supplier dialog — reuses the estimate/SO send modal in VENDOR
          MODE: the supplier's primary email is a pre-checked, removable
          recipient and the rep can add more addresses or copy themselves.
          Since 2026-08-04 the rep edits the WHOLE email (greeting, link line,
          artwork lines, sign-off) — nothing is appended server-side, and the
          missing-link soft gate is the only thing standing between a deleted
          link and a dead-end vendor email. */}
      {showSendModal && (
        <SendToCustomerModal
          title={`${isDraft ? "Send" : "Resend"} PO #${po.poNumber}`}
          subtitle="Email the purchase order to the supplier and anyone you'd like to copy."
          clientId=""
          contactsLabel="Supplier recipients"
          presetRecipients={
            vendorData?.vendor.primaryEmail
              ? [
                  {
                    email: vendorData.vendor.primaryEmail,
                    // Belt-and-suspenders alongside the server's email-scoped
                    // greeting: carry the vendor name so the primary recipient is
                    // never nameless.
                    name: vendorData.vendor.name,
                    label: vendorData.vendor.name,
                    sublabel: "Primary supplier email",
                  },
                ]
              : []
          }
          ctaLabel={
            decoConfirmPending || missingLinkConfirmPending
              ? "Send PO anyway"
              : "Send PO"
          }
          // Either armed soft gate → rust. The $0-decoration confirm already
          // expected this weight; the missing-link confirm now matches it.
          ctaVariant={
            decoConfirmPending || missingLinkConfirmPending
              ? "destructive"
              : "primary"
          }
          submitting={sendMutation.isPending}
          errorMessage={sendModalError}
          noticeMessage={sendModalNotice}
          showCopyToMe
          showBodyEditor
          composeUrl={`/pos/${id}/send-compose`}
          bodyLabel="Email to the supplier"
          linkNoun="purchase order link"
          currentUserEmail={user?.email ?? null}
          // To / CC / BCC like commonsku (Stephanie 2026-10-14): the
          // supplier's primary email and contacts are offered on each line.
          // Saved PO email templates (Stephanie 2026-10-15).
          templatesUrl="/po-templates"
          addressBook={[
            ...(vendorData?.vendor.primaryEmail
              ? [{ email: vendorData.vendor.primaryEmail, name: vendorData.vendor.name, role: "Primary supplier email" }]
              : []),
            ...(vendorData?.contacts ?? []).flatMap((c) =>
              c.email ? [{ email: c.email, name: c.name, role: c.role }] : [],
            ),
          ]}
          onCancel={() => {
            if (sendMutation.isPending) return;
            setShowSendModal(false);
            setDecoConfirmPending(false);
            setMissingLinkConfirmPending(false);
            setSendModalError(null);
            setSendModalNotice(null);
          }}
          onSubmit={(recipients, options) => {
            setBanner(null);
            // Clear stale errors but KEEP the notice: while the armed second
            // submit is in flight it still explains why the CTA reads
            // "Send PO anyway".
            setSendModalError(null);
            sendMutation.mutate({
              recipients,
              bodyHtml: options?.bodyHtml,
              copySender: options?.sendCopyToMe,
              cc: options?.cc,
              bcc: options?.bcc,
              bccProduction: options?.bccProduction,
              subject: options?.subject,
              leaveOff: options?.leaveOff,
              // Second submit after the $0-decoration soft gate sends anyway.
              skipZeroDecoration: decoConfirmPending,
              // Second submit after the missing-link soft gate sends anyway.
              skipMissingLink: missingLinkConfirmPending,
            });
          }}
        />
      )}
    </div>
  );
}

function HeaderFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-ink">{children}</dd>
    </div>
  );
}

function Field({
  label,
  value,
  wrap,
}: {
  label: string;
  value: string;
  wrap?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs text-muted">
        {label}
      </dt>
      <dd
        className={`mt-0.5 text-sm text-ink ${wrap ? "whitespace-pre-line" : ""}`}
      >
        {value}
      </dd>
    </div>
  );
}

function EditInput({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <label className="block text-xs text-muted">
      {label}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block h-10 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink"
      />
    </label>
  );
}

function EditSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}) {
  return (
    <label className="block text-xs text-muted">
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block h-10 w-full rounded-md border border-rule bg-white px-3 text-sm text-ink"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function StaffDecoration({ decoration: d }: { decoration: PoDecoration }) {
  return (
    <div className="rounded-md border border-[#e7e7e4] bg-[#fafaf8] p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2 text-ink">
        {d.location && <span className="font-medium">{d.location}</span>}
        {d.imprintType && (
          <span className="rounded bg-[#f1f1ee] px-2 py-0.5 text-[11px] font-medium text-ink">
            {d.imprintType}
          </span>
        )}
        {colorCountWords(d.colorCount) && (
          <span className="text-xs text-muted">{colorCountWords(d.colorCount)}</span>
        )}
      </div>
      {d.inkColors.length > 0 && (
        <p className="mt-1 text-xs text-muted">{d.inkColors.join(", ")}</p>
      )}
      {d.charges.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {d.charges.map((c, i) => (
            <li
              key={`${d.id}-c-${i}`}
              className="flex justify-between text-xs text-muted"
            >
              <span>
                {/* Just the charge's own name (Stephanie 2026-10-27). */}
                {poChargeName(c)}
                {c.billingModel === "per_piece" ? " / pc" : ""}
                {/* A typed count (Stephanie 2026-10-27). */}
                {c.quantity != null && (c.billingModel === "per_piece" || c.quantity !== 1) ? ` × ${c.quantity.toLocaleString()}` : ""}
              </span>
              <span className="tabular-nums">{formatMoney(c.amount)}</span>
            </li>
          ))}
        </ul>
      )}
      {/* Artwork Details (T16) — the exact spec block the vendor sees on
          /po/:uuid (same shared helpers, so the two renderings can't drift).
          Omitted entirely when there's nothing to show; the artwork link
          lives inside it, mirroring the public layout. */}
      {hasArtworkDetails(d) && (
        <div className="mt-2 rounded border border-[#e7e7e4] bg-white p-3">
          <p className="mb-1.5 text-[11px] font-medium text-muted">
            Artwork Details
          </p>
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
            <StaffArtworkDetailRow label="Logo" value={d.logoName} />
            <StaffArtworkDetailRow label="Size" value={imprintSizeLabel(d) || null} />
            <StaffArtworkDetailRow label="Logo color" value={d.logoColor} />
            <StaffArtworkDetailRow label="Re-order" value={d.repeatLogo} />
            <StaffArtworkDetailRow
              label="Proof"
              value={proofTypeLabel(d.proofType) || null}
            />
          </dl>
          {d.artwork && (
            <div className="mt-2">
              <StaffArtworkLink artwork={d.artwork} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** One label/value row inside the staff Artwork Details block; renders nothing
 *  when the value is empty so the block never shows a bare label (mirrors
 *  PublicPOPage's ArtworkDetailRow). */
function StaffArtworkDetailRow(props: { label: string; value: string | null }) {
  const v = props.value?.trim();
  if (!v) return null;
  return (
    <>
      <dt className="text-[11px] text-muted">
        {props.label}
      </dt>
      <dd className="font-medium text-ink">{v}</dd>
    </>
  );
}

function StaffArtworkLink({ artwork: a }: { artwork: PoArtwork }) {
  const label = (
    <span className="inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-rule bg-white px-2.5 py-1.5 text-xs font-medium text-ink">
      <Download className="h-3.5 w-3.5" />
      <span className="max-w-[180px] truncate">{a.filename}</span>
      {a.proofRequired && (
        <span className="text-[11px] font-normal text-muted">
          · proof req
        </span>
      )}
    </span>
  );
  if (a.url) {
    return (
      <a
        href={a.url}
        target="_blank"
        rel="noopener noreferrer"
        download={a.filename}
        className="hover:opacity-90"
      >
        {label}
      </a>
    );
  }
  return label;
}
